import React from 'react';

// The volume view. A board stops being scannable well before the 200 rows the endpoint
// used to cap at silently, and nobody reads 300 concepts across seven columns - so past
// triage size this is the tool: dense rows, sorted, paged, with the totals stated.
const TONE = {
  build_stopped: 'text-orange-700 dark:text-orange-300',
  review_overdue: 'text-orange-700 dark:text-orange-300',
};

const SORTS = [
  { id: 'updated_at', label: 'Last updated' },
  { id: 'status', label: 'Stage' },
  { id: 'price', label: 'Price' },
  { id: 'title', label: 'Title' },
];

// No client-side sort. This used to reorder whichever page it had been handed, so
// "Title ascending" arranged fifty rows while the title that belonged first sat on page
// two - a control labelled "Sort" giving a wrong global order. The server sorts before
// paging now, so the rows arrive in the right order and this component just renders them.

const Th = ({ children, className = '' }) => (
  <th className={`text-left font-semibold px-3 py-2 whitespace-nowrap ${className}`}>{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-3 py-2 align-top ${className}`}>{children}</td>
);

export default function ConceptsList({
  concepts, total, page, perPage, truncated, lanes,
  sortKey, sortDir, onSort, onPage, onOpen,
}) {
  const laneLabel = (id) => lanes.find((l) => l.id === id)?.label || id;
  const rows = concepts;
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  const lastPage = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        {/* Says outright what is on screen versus what exists. The board used to show a
            count of what survived an invisible cap, which read as "this is everything". */}
        <span className="text-[11px] text-gray-500">
          {total === 0 ? 'No concepts match' : `Showing ${from}-${to} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">Sort</span>
          <select
            value={`${sortKey}:${sortDir}`}
            onChange={(e) => { const [k, d] = e.target.value.split(':'); onSort(k, d); }}
            className="text-[11px] bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-800"
          >
            {SORTS.flatMap((s) => ([
              <option key={`${s.id}:desc`} value={`${s.id}:desc`}>{s.label} ↓</option>,
              <option key={`${s.id}:asc`} value={`${s.id}:asc`}>{s.label} ↑</option>,
            ]))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
            <tr>
              <Th>Concept</Th>
              <Th>Stage</Th>
              <Th>Program</Th>
              <Th>Delivery</Th>
              <Th>Class target</Th>
              <Th>Build</Th>
              <Th>Blocker</Th>
              <Th>With</Th>
              <Th className="text-right">Price</Th>
              <Th>Updated</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><Td className="text-gray-400 py-6" >Nothing here. Try a different search or filter.</Td></tr>
            )}
            {rows.map((c) => (
              <tr
                key={c.id}
                onClick={() => onOpen(c.id)}
                className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
              >
                <Td>
                  {/* A real button, not just a clickable row: the row itself cannot take
                      focus, so without this a keyboard user could not open a concept from
                      this view at all. stopPropagation keeps the row handler from firing
                      twice on a mouse click. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpen(c.id); }}
                    className="font-semibold leading-snug text-left hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    {c.title}
                  </button>
                  <div className="text-[10px] text-gray-400">{c.id}</div>
                  {c.client_name && <div className="text-[10px] text-gray-500">{c.client_name}</div>}
                </Td>
                <Td className="whitespace-nowrap">{laneLabel(c.lane)}</Td>
                <Td className="whitespace-nowrap">{c.program || '—'}</Td>
                <Td className="whitespace-nowrap">{c.delivery || '—'}</Td>
                <Td>
                  {c.class_target?.disposition ? (
                    <span>
                      {c.class_target.disposition === 'revision' ? '↻ ' : '✦ '}
                      {c.class_target.name || (c.class_target.disposition === 'revision' ? 'existing class' : 'new class')}
                      {!c.class_target.confirmed && (
                        <span className="text-orange-600 dark:text-orange-400"> · unconfirmed</span>
                      )}
                    </span>
                  ) : <span className="text-gray-400">not set</span>}
                </Td>
                <Td className="whitespace-nowrap">
                  {c.production
                    ? `${c.production.artifact_count || 0} · ${c.production.stalled ? 'stopped' : String(c.production.status).replace(/_/g, ' ')}`
                    : <span className="text-gray-400">—</span>}
                </Td>
                <Td className={`whitespace-nowrap ${TONE[c.blocker?.code] || 'text-orange-600 dark:text-orange-400'}`}>
                  {c.blocker ? c.blocker.label : <span className="text-gray-400">—</span>}
                </Td>
                <Td className="whitespace-nowrap">{c.owner || <span className="text-gray-400">—</span>}</Td>
                <Td className="text-right whitespace-nowrap">
                  {c.approved_price != null ? (
                    <span className="font-bold text-green-600">${Number(c.approved_price).toLocaleString()}</span>
                  ) : c.weighted_value != null ? (
                    <span className="text-gray-400" title="Pipeline forecast, not the price">
                      ~${Number(c.weighted_value).toLocaleString()}
                    </span>
                  ) : <span className="text-gray-400">—</span>}
                </Td>
                <Td className="whitespace-nowrap text-gray-500">
                  {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(total > perPage || truncated) && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => onPage(page - 1)} disabled={page <= 1}
            className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >← Previous</button>
          <span className="text-[11px] text-gray-500">Page {page} of {lastPage}</span>
          <button
            onClick={() => onPage(page + 1)} disabled={page >= lastPage}
            className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >Next →</button>
        </div>
      )}
    </div>
  );
}
